import { useState, useRef, useEffect } from 'react'

const MAIN_SELECTOR = '.-main'

const BORDER_WIDTH = 1

// The report column, which is already laid out before the first render -- so it is read straight
// out and serves as the opening figure, with nothing to wait for.
const measureReportColumn = () => {
  const element = document.querySelector(MAIN_SELECTOR)
  if (!element) return null

  const styles = window.getComputedStyle(element)
  const paddingLeft = parseFloat(styles.paddingLeft)
  const paddingRight = parseFloat(styles.paddingRight)

  // clientWidth includes content + padding, but excludes borders/scrollbars
  return element.clientWidth - paddingLeft - paddingRight - 2 * BORDER_WIDTH
}

// Measures the box the component is drawn in.
//
// The report column answers for the first render: it is already laid out, so the block is drawn at
// a real size straight away rather than at a default it has to correct. It is an opening figure and
// not the answer, though -- a component narrower than the column, or one whose box moves later, is
// only told by watching its own box, so the watch is kept for as long as the component lives.
//
// `ref` lets a component that already holds a ref to its box hand it in, rather than wrapping
// itself in a div only to satisfy this hook.
export const useComponentWidth = ({ ref, defaultWidth } = {}) => {
  // read once, so the first render and the effect below cannot disagree
  const columnRef = useRef()
  if (undefined === columnRef.current) columnRef.current = measureReportColumn()

  const [width, setWidth] = useState(() => columnRef.current || defaultWidth)

  const localRef = useRef(null)
  const componentRef = ref || localRef

  const rafRef = useRef(null)
  const resizeObserverRef = useRef(null)

  // the observer is the only part that has to wait for a mount
  useEffect(() => {
    resizeObserverRef.current?.disconnect()

    // rAF defers the measure out of the observer's frame and round+compare drops sub-pixel
    // deltas, so observing the container that holds the redrawn <svg> can't feed back into itself.
    // eslint-disable-next-line compat/compat
    resizeObserverRef.current ||= new window.ResizeObserver(entries => {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(() => {
        const rect = entries?.[0]?.target?.getBoundingClientRect()

        if (!rect?.width) return

        const next = Math.round(rect.width)
        setWidth(prev => (prev === next ? prev : next))
      })
    })
    if (componentRef.current) {
      resizeObserverRef.current.observe(componentRef.current)
    }

    return () => {
      cancelAnimationFrame(rafRef.current)
      resizeObserverRef.current?.disconnect()
    }
  }, [componentRef.current])

  return {
    componentRef,
    width
  }
}
